<template>
  <BaseProgressBar v-if="isLoading" :message="t('general.loading')" />
  <template v-if="hasPeriodIdsEnabled && !isLoading">
    <v-row class="mb-4">
      <v-col cols="5">
        <!-- Assortment period numbers / auto-populate column -->
        <div>
          <v-row class="ma-0">
            <h3 class="dates-selection__input-heading mb-2">
              {{ tkey('assortmentPeriodNumbers') }}
            </h3>
          </v-row>
          <v-row class="ma-0">
            <v-autocomplete
              class="rtls-select--multi-select"
              chips
              closable-chips
              multiple
              hide-details
              :model-value="wpModelSelections.periodIds || []"
              :items="parsedPeriodIdsOptions"
              item-title="text"
              item-value="value"
              :style="{ 'max-width': '280px', width: '20rem' }"
              :placeholder="t('general.select')"
              :menu-props="{ dense: true, contentClass: 'rtls-multi-select-list' }"
              @update:model-value="val => updateModel(val, 'periodIds')"
            >
              <template #chip="{ item, index, props }">
                <v-chip v-if="index === 0" v-bind="props">
                  <span>{{ item.title }}</span>
                </v-chip>
                <span v-if="index === 1" class="text-grey text-caption">
                  (+{{ wpModelSelections.periodIds.length - 1 }})
                </span>
              </template>
            </v-autocomplete>

            <v-autocomplete
              v-if="betaAutoGenerateWorkpackagePeriodDatesEnabled"
              class="rtls-select--multi-select ml-3"
              hide-details
              :model-value="wpModelSelections.selectedChain"
              :items="chainOptions"
              :width="130"
              :max-width="200"
              placeholder="Select chain"
              :menu-props="{ dense: true, contentClass: 'rtls-multi-select-list' }"
              @update:model-value="val => updateModel(val, 'selectedChain')"
            />

            <v-tooltip
              :disabled="!automaticDatesDisabledTooltip"
              :text="automaticDatesDisabledTooltip"
            >
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    v-if="betaAutoGenerateWorkpackagePeriodDatesEnabled"
                    :disabled="
                      !selectedPeriodHasSingleDateRange || !wpModelSelections.selectedChain
                    "
                    :loading="isRunningAutoPopulate"
                    variant="flat"
                    primary
                    size="small"
                    class="ml-3 autodate-btn"
                    @click="runAutoPopulate"
                  >
                    {{ tkey('automaticDatesAndAgsBtn') }}
                  </v-btn>
                </span>
              </template>
            </v-tooltip>
            <span
              v-if="betaAutoGenerateWorkpackagePeriodDatesEnabled"
              class="d-flex align-center ml-1"
            >
              <AssortmentTooltip
                :title="t('tooltips.autoPopulate.title')"
                :tooltip-sections="autoPopulateTooltipSections"
              />
            </span>
          </v-row>
        </div>
        <!-- End of assortment period numbers column -->
      </v-col>
    </v-row>
  </template>
  <div v-if="!isLoading" class="dates-selection pa-2 border-s border-e border-opacity-25">
    <v-row no-gutters class="pb-2">
      <!-- Snapshot date column -->
      <v-col cols="2" class="pr-4 d-flex">
        <div>
          <h3 class="dates-selection__input-heading">
            {{ tkey('snapshotDateLabel') }}

            <!-- Snapshot date tooltip -->
            <AssortmentTooltip
              :title="t('tooltips.snapshotDate.title')"
              :tooltip-sections="snapshotDateTooltipSections"
            />
          </h3>
          <div data-id-e2e="dateSnapshot" class="dates-selection__input">
            <RtlsCalendarPicker
              :key="nameKey"
              :value="wpDateSelections.snapshotDate"
              :allowed-dates="formattedSnapshotDates"
              :display-date-format="longDateFormat"
              :loading="isSnapshotDateLoading"
              @input="val => handleDateUpdate(val, 'snapshotDate')"
            />
          </div>
        </div>
        <!-- End of snapshot date column -->
      </v-col>
      <v-col cols="3" class="pr-4">
        <!-- Fill according to column -->
        <div>
          <h3 class="dates-selection__input-heading">
            {{ tkey('fillLabel') }}

            <!-- Fill according to tooltip -->
            <AssortmentTooltip
              :title="t('tooltips.fillAccordingTo.title')"
              :tooltip-sections="fillAccordingToTooltipSections"
            />
          </h3>
          <v-select
            :model-value="wpModelSelections.fillInSelection"
            :placeholder="t('sizeTypes.noOption')"
            :items="sizeSelectItems"
            :style="{ width: '280px', 'max-width': '280px' }"
            hide-details
            :list-props="{ density: 'compact', lines: false, class: 'py-0' }"
            :menu-props="{ location: 'top', origin: 'overlap' }"
            @update:model-value="val => updateModel(val, 'fillInSelection')"
          >
            <template #append>
              <v-icon
                :icon="sizeTypeIconMap[wpModelSelections.fillInSelection] || '$empty-cube'"
                size="32"
              />
            </template>
          </v-select>
        </div>
        <!-- End of fill according to column -->
      </v-col>
      <v-col
        v-if="previousWorkpackageLinkingEnabled && !isLoading"
        cols="2"
        class="pr-4"
        data-test-id="previous-workpackage-col"
      >
        <!-- Previous workpackage column -->
        <h3 class="mb-2">
          {{ tkey('previousWorkpackageLabel') }}

          <!-- Previous workpackage tooltip -->
          <AssortmentTooltip
            :title="t('tooltips.previousWorkpackage.title')"
            :tooltip-sections="previousWorkpackageTooltipSections"
          />
        </h3>
        <AssortmentSearch
          id="workpackagesSearch"
          ref="assortmentSearch"
          item-text="name"
          :item-label="wp => wp.name"
          :disabled="isPreviousWorkpackageSearchDisabled"
          :on-search="onPreviousWorkpackageSearch"
          async-search
          search-on-demand
          :results-limit="100"
          :placeholder="tkey('selectPreviousWorkpackagePlaceholder')"
          :on-selection="handlePreviousWorkpackageChange"
          :initial-item="initialPreviousWorkpackage"
        />
        <!-- End of previous workpackage Column -->
      </v-col>

      <v-col
        v-if="previousWorkpackageLinkingEnabled && !isLoading && showScenarioSelection"
        cols="2"
        data-test-id="previous-scenario-col"
      >
        <!-- Previous Scenario Column -->
        <h3 class="mb-2">
          {{ tkey('previousScenarioLabel') }}

          <!-- Previous scenario tooltip -->
          <AssortmentTooltip
            :title="t('tooltips.previousScenario.title')"
            :tooltip-sections="previousScenarioTooltipSections"
          />
        </h3>
        <v-autocomplete
          class="rtls-select--multi-select"
          hide-details
          :model-value="wpModelSelections.linkedPreviousScenarioId || null"
          :disabled="isPreviousScenarioSearchDisabled"
          :items="previousWorkpackageScenarios"
          :loading="isLoadingScenarios"
          item-title="name"
          item-value="_id"
          :style="{ 'max-width': '350px' }"
          :placeholder="tkey('selectPreviousScenarioPlaceholder')"
          :menu-props="{ dense: true, contentClass: 'rtls-multi-select-list' }"
          @update:model-value="val => updateModel(val, 'linkedPreviousScenarioId')"
        >
          <!-- Add a ★ icon to the left of the selected scenario when it's final -->
          <template #prepend-inner>
            <v-icon
              v-if="isFinalScenario(wpModelSelections.linkedPreviousScenarioId)"
              icon="mdi-star"
              color="primary"
              size="18"
              class="pl-2 opacity-100"
            />
          </template>
          <!-- Add a ★ icon to the left of any scenarios in the dropdown list which are final -->
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :class="finalScenarioIds?.length ? 'pl-2' : ''">
              <template #prepend>
                <v-icon
                  v-if="isFinalScenario(item.value)"
                  icon="mdi-star"
                  color="primary"
                  size="18"
                  class="mr-1 opacity-100"
                />
                <!-- Add empty space to align the text correctly when the final scenario icon is present -->
                <span v-else-if="finalScenarioIds?.length" class="mr-7" />
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
        <!-- End of previous scenario Column -->
      </v-col>
      <v-col cols="1" offset="1">
        <!-- Go Live column -->
        <div
          v-if="bundlesEnabled && selectedWorkpackage.bundleId && selectedWorkpackage.templateId"
        >
          <h3 class="dates-selection__input-heading">
            {{ t('bundlesPage.goLive') }}

            <!-- Go live date tooltip -->
            <AssortmentTooltip
              :title="t('tooltips.targetLaunchDate.title')"
              :tooltip-sections="targetLaunchDateTooltipSections"
            />
          </h3>
          <div data-id-e2e="targetLaunchDate" class="dates-selection__input">
            <RtlsCalendarPicker
              :key="nameKey"
              :value="wpDateSelections.targetLaunchDate"
              :display-date-format="longDateFormat"
              @input="val => handleDateUpdate(val, 'targetLaunchDate')"
            />
          </div>
        </div>
        <!-- End of Go Live column -->
      </v-col>
    </v-row>

    <v-divider />

    <v-row no-gutters>
      <!-- Performance Date -->
      <v-col cols="4" class="pr-4">
        <v-card class="col-box">
          <h3 class="mt-3 dates-selection__input-heading">
            {{ tkey('performancePeriodLabel') }}
            <span>{{ tkey('performancePeriodLabelWeeks') }}</span>

            <!-- Performance period tooltip -->
            <AssortmentTooltip
              :title="t('tooltips.performancePeriod.title')"
              :tooltip-sections="performancePeriodTooltipSections"
            />
          </h3>
          <div class="dates-selection__input-container">
            <!-- Performance start date -->
            <div
              data-id-e2e="datePerformance"
              class="dates-selection__input dates-selection__input--longer"
            >
              <span>{{ tkey('startLabel') }}:</span>
              <span v-if="useEndDateSelection" class="dates-selection__input_startdate">
                {{ formatDate(wpDateSelections.performanceStartDate, longDateFormat) }}
              </span>
              <RtlsCalendarPicker
                v-if="!useEndDateSelection"
                :key="nameKey"
                :value="wpDateSelections.performanceStartDate"
                :allowed-dates="allowedStartAndExcludedDates"
                :display-date-format="longDateFormat"
                @input="val => handleDateUpdate(val, 'performanceStartDate')"
              />
            </div>

            <!-- Performance end date -->
            <div
              v-if="useEndDateSelection"
              data-id-e2e="datePerformance"
              class="dates-selection__input dates-selection__input--longer"
            >
              <span>{{ tkey('endLabel') }}:</span>
              <RtlsCalendarPicker
                :key="nameKey"
                :value="wpDateSelections.performanceEndDate"
                :allowed-dates="allowedEndDates"
                :display-date-format="longDateFormat"
                @input="val => handleDateUpdate(val, 'performanceEndDate')"
              />
            </div>

            <span v-if="!useEndDateSelection" class="mr-0">
              {{ tkey('endLabel') }}:
              <v-tooltip
                :model-value="endDatesExceeded.performance"
                :disabled="
                  !endDatesExceeded.performance || !!wpDateSelections.performancePeriodPreSet
                "
                :hidden="isApplyingPreSet || !!wpDateSelections.performancePeriodPreSet"
                location="right"
              >
                <template #activator="{ props }">
                  <b v-bind="props">
                    {{ formatDate(wpDateSelections.performanceEndDate, longDateFormat) }}
                  </b>
                </template>
                <span v-if="endDatesExceeded.performance">{{ tkey('maxWeekWarning') }}</span>
              </v-tooltip>
            </span>
          </div>

          <WeekSelector
            v-model="wpDateSelections.performanceDateWeeks"
            data-id-e2e="btnPerformanceWeeks"
            :model-value="wpDateSelections.performanceDateWeeks"
            :week-options="defaultWeekOptions"
            :placeholder="tkey('custom')"
            @update:model-value="val => handleDateUpdate(val, 'performanceDateWeeks')"
          />
        </v-card>

        <!-- excluding date -->
        <v-card class="pa-2 pl-0 pb-0 col-box no-border">
          <div data-id-e2e="dateExclude" class="dates-selection__exclude-container">
            <h3 class="dates-selection__input-heading">{{ tkey('excludingLabel') }}:</h3>
            <MultipleDatesSelector
              multiple
              enable-week-selection
              mondays-only
              show-actions
              :value="wpDateSelections.performanceExcludedDates"
              :allowed-dates="allowedStartAndExcludedDates"
              @input="val => handleDateUpdate(val, 'performanceExcludedDates')"
            />
          </div>
        </v-card>
        <!-- end excluding date -->

        <!-- Performance presets -->
        <v-card
          v-if="performancePeriodPreSetSelectMenuEnabled"
          class="pa-2 pl-0 pt-0 col-box no-border"
        >
          <div data-id-e2e="datePresets" class="dates-selection__exclude-container">
            <h3 class="dates-selection__input-heading">Pre-sets:</h3>
            <v-select
              v-model="wpDateSelections.performancePeriodPreSet"
              :items="
                performancePeriodPreSetSelectOptions.map(preset => ({
                  title: preset.desc,
                  value: preset.id,
                }))
              "
              :style="{ width: '220px', 'max-width': '220px' }"
              :placeholder="tkey('performancePeriodPreSetsPlaceholder')"
              :list-props="{ density: 'compact', lines: false, class: 'py-0' }"
              :menu-props="{ location: 'top', origin: 'overlap' }"
              hide-details
              @update:model-value="val => handlePerformancePreSetsUpdate(val)"
            />
          </div>
        </v-card>
      </v-col>

      <!-- learning period-->
      <v-col v-if="hasSeasonalityEnabled && !isSimpleSwapsWP" cols="4" class="pr-4">
        <v-card class="col-box">
          <!-- learning date -->
          <h3 class="mt-3 dates-selection__input-heading">
            {{ tkey('learningPeriodLabel') }}
            <span>{{ tkey('learningPeriodLabelWeeks') }}</span>

            <!-- Learning period tooltip -->
            <AssortmentTooltip
              :title="t('tooltips.learningPeriod.title')"
              :tooltip-sections="learningPeriodTooltipSections"
            />
          </h3>
          <div class="dates-selection__input-container">
            <!-- Learning period start date -->
            <div
              data-id-e2e="dateLearning"
              class="dates-selection__input dates-selection__input--longer"
            >
              <span>{{ tkey('startLabel') }}:</span>
              <span v-if="useEndDateSelection" class="dates-selection__input_startdate">
                {{ formatDate(wpDateSelections.learningStartDate, longDateFormat) }}
              </span>
              <RtlsCalendarPicker
                v-if="!useEndDateSelection"
                :key="`learning-start-date-${nameKey}`"
                :value="wpDateSelections.learningStartDate"
                :allowed-dates="allowedStartAndExcludedDates"
                :display-date-format="longDateFormat"
                @input="val => handleDateUpdate(val, 'learningStartDate')"
              />
            </div>

            <!-- Learning period end date -->
            <div
              data-id-e2e="dateLearningEnd"
              class="dates-selection__input dates-selection__input--longer"
            >
              <span>{{ tkey('endLabel') }}:</span>
              <RtlsCalendarPicker
                v-if="useEndDateSelection"
                :key="nameKey"
                :value="wpDateSelections.learningEndDate"
                :allowed-dates="allowedEndDates"
                :display-date-format="longDateFormat"
                @input="val => handleDateUpdate(val, 'learningEndDate')"
              />
            </div>

            <span v-if="!useEndDateSelection" class="mr-0">
              {{ tkey('endLabel') }}:
              <v-tooltip
                :model-value="endDatesExceeded.learning"
                :disabled="!endDatesExceeded.learning"
                location="right"
              >
                <template #activator="{ props }">
                  <b v-bind="props">
                    {{ formatDate(wpDateSelections.learningEndDate, longDateFormat) }}
                  </b>
                </template>
                <span v-if="endDatesExceeded.learning">{{ tkey('maxWeekWarning') }}</span>
              </v-tooltip>
            </span>
          </div>

          <WeekSelector
            v-model="wpDateSelections.learningDateWeeks"
            data-id-e2e="btnLearningWeeks"
            :model-value="wpDateSelections.learningDateWeeks"
            :week-options="defaultWeekOptions"
            :placeholder="tkey('custom')"
            @update:model-value="val => handleDateUpdate(val, 'learningDateWeeks')"
          />
        </v-card>

        <!-- learning excluding date -->
        <v-card class="pa-2 pl-0 col-box no-border">
          <div data-id-e2e="dateExclude" class="dates-selection__exclude-container">
            <h3 class="dates-selection__input-heading">{{ tkey('excludingLabel') }}</h3>
            <MultipleDatesSelector
              multiple
              enable-week-selection
              mondays-only
              show-actions
              :value="wpDateSelections.learningExcludedDates"
              :allowed-dates="allowedStartAndExcludedDates"
              @input="val => handleDateUpdate(val, 'learningExcludedDates')"
            />
          </div>
        </v-card>
        <!-- end excluding date -->
      </v-col>
      <!-- end learning period -->

      <!-- switching period -->
      <v-col cols="4">
        <v-card class="col-box">
          <h3 class="dates-selection__input-heading mt-3">
            {{ tkey('switchingPeriodLabel') }}
            <span>{{ tkey('switchingPeriodLabelWeeks') }}</span>

            <!-- Switching period tooltip -->
            <AssortmentTooltip
              :title="t('tooltips.switchingPeriod.title')"
              :tooltip-sections="switchingPeriodTooltipSections"
            />
          </h3>
          <v-row no-gutters>
            <div class="dates-selection__input-container mb-0">
              <!-- Switching period start date -->
              <div
                data-id-e2e="dateSwitching"
                class="dates-selection__input dates-selection__input--longer"
              >
                <span>{{ tkey('startLabel') }}:</span>
                <span v-if="useEndDateSelection" class="dates-selection__input_startdate">
                  {{ formatDate(wpDateSelections.switchingStartDate, longDateFormat) }}
                </span>
                <RtlsCalendarPicker
                  v-if="!useEndDateSelection"
                  :key="nameKey"
                  :value="wpDateSelections.switchingStartDate"
                  :allowed-dates="allowedStartAndExcludedDates"
                  :display-date-format="longDateFormat"
                  @input="val => handleDateUpdate(val, 'switchingStartDate')"
                />
              </div>

              <!-- Switching period end date -->
              <div
                data-id-e2e="dateSwitchingEnd"
                class="dates-selection__input dates-selection__input--longer"
              >
                <span>{{ tkey('endLabel') }}:</span>
                <RtlsCalendarPicker
                  v-if="useEndDateSelection"
                  :key="nameKey"
                  :value="wpDateSelections.switchingEndDate"
                  :allowed-dates="allowedEndDates"
                  :display-date-format="longDateFormat"
                  @input="val => handleDateUpdate(val, 'switchingEndDate')"
                />
              </div>

              <span v-if="!useEndDateSelection" class="wp-scope__end-label">
                {{ tkey('endLabel') }}:
                <v-tooltip
                  :model-value="endDatesExceeded.switching"
                  :disabled="!endDatesExceeded.switching"
                  location="right"
                >
                  <template #activator="{ props }">
                    <b v-bind="props">
                      {{ formatDate(wpDateSelections.switchingEndDate, longDateFormat) }}
                    </b>
                  </template>
                  <span v-if="endDatesExceeded.switching">{{ tkey('maxWeekWarning') }}</span>
                </v-tooltip>
              </span>
            </div>
          </v-row>

          <WeekSelector
            v-model="wpDateSelections.switchingDateWeeks"
            data-id-e2e="btnSwitchingWeeks"
            :model-value="wpDateSelections.switchingDateWeeks"
            :week-options="doubledWeekOptions"
            :placeholder="tkey('custom')"
            @update:model-value="val => handleDateUpdate(val, 'switchingDateWeeks')"
          />
        </v-card>
        <!-- end switching period -->
      </v-col>
    </v-row>

    <!-- Additional Settings -->
    <template v-if="calculateTrendEnabled || useMarkdownAndWasteToCalculateMarginAndPriceEnabled">
      <v-divider />
      <v-row no-gutters class="pt-1">
        <!-- Max trend -->
        <v-col
          v-if="calculateTrendEnabled"
          cols="2"
          class="dates-selection__maximum-trend-container"
        >
          <h3 class="dates-selection__input-heading">
            {{ tkey('maximumTrendLabel') }} <span>({{ tkey('maximumTrendUnit') }})</span>
          </h3>
          <div class="dates-selection__input">
            <AssortmentTextField
              v-model="wpModelSelections.maximumTrend"
              :rules="rules"
              bg-color="white"
              variant="filled"
              @update:model-value="val => updateModel(val, 'maximumTrend')"
            />
          </div>
        </v-col>
        <!-- End max trend -->
        <!-- Markdown and waste-->
        <v-col v-if="useMarkdownAndWasteToCalculateMarginAndPriceEnabled" cols="3">
          <v-switch
            v-model="wpModelSelections.useMarkdownAndWasteToCalculateMarginAndPrice"
            hide-details
            class="pl-1"
            density="compact"
            color="primary"
            :label="tkey('useMarkdownAndWasteLabel')"
            @update:model-value="
              val => updateModel(val, 'useMarkdownAndWasteToCalculateMarginAndPrice')
            "
          />
        </v-col>
        <!-- End markdown and waste -->
      </v-row>
    </template>
    <!-- End additional settings -->
  </div>

  <!-- Snapshot date change warning dialog -->
  <DependencyTreeFeedbackModal
    :value="dependencyTreeModalOpen"
    :results="dependencyTreeFeedback"
    page="snapshotDateChanged"
    @close="closeDependencyTreeModal"
    @commit="handleSnapshotDateChange(true)"
  />
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { isEqual, size, isEmpty, sortBy } from 'lodash';

// enums
import SIZE_TYPES from '@enums/size-types';
import chainLevels from '@enums/chain-levels';

// composables
import { useFeature } from '@/composables/use-feature';
import { useDayjs } from '@/composables/use-dayjs';
import { useInputValidations } from '@/composables/input-validations';
import { useInternationalization } from '@/composables/use-internationalization';
import { useWorkpackageScopeTooltipOptions } from '@/composables/use-tooltip-options';
import { useVuetifyEffects } from '@/composables/use-vuetify-effects';

// stores
import { useContextStore } from '@/stores/context';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useWorkpackageProductsStore } from '@/stores/workpackage-products';
import { useWorkpackageScenarioSelectStore } from '@/stores/workpackage-scenario-select';
import { useToolDataStore } from '@/stores/tool-data';
import { useDependencyTreeStore } from '@/stores/dependency-tree';

const maximumTrendLimit = 100;
const defaultWeekOptions = [52, 26, 13, 0];
const doubledWeekOptions = defaultWeekOptions.map(x => x * 2);

export default {
  setup() {
    const contextStore = useContextStore();

    // FRESH-TODO: Unused?
    const workpackagesStore = useWorkpackagesStore();
    const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();
    const workpackageProductsStore = useWorkpackageProductsStore();

    const {
      hasSeasonalityEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      calculateTrendEnabled,
      betaAutoGenerateWorkpackagePeriodDatesEnabled,
      previousWorkpackageLinkingEnabled,
      performancePeriodPreSetSelectMenuEnabled,
      performancePeriodPreSetSelectOptions,
      showSetByEndDateToggle,
      useMarkdownAndWasteToCalculateMarginAndPriceEnabled,
    } = useFeature();
    const { toNearestSunday, formatUtcDate, formatDate, getIsoWeekday, checkDateIsSameOrBefore } =
      useDayjs();
    const { t, tkey } = useInternationalization('workpackagePage.scope');

    const { required, isNumber, isLessThan, isGreaterOrEqual } = useInputValidations();

    const {
      snapshotDateTooltipSections,
      fillAccordingToTooltipSections,
      targetLaunchDateTooltipSections,
      performancePeriodTooltipSections,
      learningPeriodTooltipSections,
      switchingPeriodTooltipSections,
      autoPopulateTooltipSections,
      previousWorkpackageTooltipSections,
      previousScenarioTooltipSections,
    } = useWorkpackageScopeTooltipOptions();

    const { removeVChipItem } = useVuetifyEffects();

    return {
      // stores
      // FRESH-TODO: unused?
      workpackagesStore,
      workpackageScenarioSelectStore,
      workpackageProductsStore,

      // features
      hasSeasonalityEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      calculateTrendEnabled,
      betaAutoGenerateWorkpackagePeriodDatesEnabled,
      bundlesEnabled: contextStore.bundlesEnabled,
      previousWorkpackageLinkingEnabled,
      performancePeriodPreSetSelectMenuEnabled,
      performancePeriodPreSetSelectOptions,
      showSetByEndDateToggle,
      useMarkdownAndWasteToCalculateMarginAndPriceEnabled,

      // date utils
      toNearestSunday,
      formatDate,
      formatUtcDate,
      getIsoWeekday,
      checkDateIsSameOrBefore,

      // test
      defaultWeekOptions,
      doubledWeekOptions,

      // input validations
      required,
      isNumber,
      isLessThan,
      isGreaterOrEqual,

      // tooltip sections
      autoPopulateTooltipSections,
      snapshotDateTooltipSections,
      fillAccordingToTooltipSections,
      targetLaunchDateTooltipSections,
      performancePeriodTooltipSections,
      learningPeriodTooltipSections,
      switchingPeriodTooltipSections,
      previousWorkpackageTooltipSections,
      previousScenarioTooltipSections,

      // other
      t,
      tkey,
      removeVChipItem,
      chainLevels,
    };
  },

  data() {
    return {
      nameKey: false,
      isLoading: false,
      isLoadingScenarios: false,
      isRunningAutoPopulate: false,
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
      isSnapshotDateLoading: false,
      pendingSnapshotDateValue: null,
      initialPreviousWorkpackage: null, // a reference to render WP search component on init
      useMarkdownAndWasteToCalculateMarginAndPrice: false,
      sizeTypeIconMap: {
        [SIZE_TYPES.linearSpace]: '$linear-space',
        [SIZE_TYPES.cubicSpace]: '$cubic-space',
        [SIZE_TYPES.horizontalSpace]: '$horizontal-space',
        [SIZE_TYPES.frontalSpace]: '$frontal-space',
        [SIZE_TYPES.productCount]: '$empty-cube',
      },
      rules: [
        this.required,
        v => this.isGreaterOrEqual(v, 0),
        v => this.isLessThan(v, maximumTrendLimit),
        this.isNumber,
      ],
    };
  },

  computed: {
    ...mapState(useContextStore, ['getDateFormats']),
    ...mapState(useToolDataStore, { periodIdsOptions: 'periodIds', periodDates: 'periodDates' }),
    ...mapState(useWorkpackagesStore, [
      'isSimpleSwapsWP',
      'wpDateSelections',
      'wpModelSelections',
      'workpackageCopy',
      'selectedWorkpackage',
      'latestSnapshotDate',
      'snapshotDates',
      'endDatesExceeded',
      'isWorkpackageSetupRunning',
      'workpackages',
      'archivedWorkpackages',
      'additionalDownloadedWorkpackages',
      'isApplyingPreSet',
      'useEndDateSelection',
      'formattedSnapshotDates',
      'getPreviousChainedWorkpackage',
    ]),
    ...mapState(useWorkpackageScenarioSelectStore, ['finalScenarioIds', 'scenariosByWorkpackage']),

    parsedPeriodIdsOptions() {
      const periodIds = this.periodIdsOptions.map(el => {
        return {
          text: el,
          value: el,
        };
      });

      if (this.betaHasMandatoryPeriodIdsEnabled) {
        periodIds.unshift({
          text: this.tkey('noPeriodID'),
          value: null,
        });
      }

      return periodIds;
    },

    chainOptions() {
      return sortBy(chainLevels.workpackageSetupChainLevels);
    },

    automaticDatesDisabledTooltip() {
      const noDatesSelectedValue = [null];
      const { periodIds, selectedChain } = this.wpModelSelections;
      if (isEmpty(periodIds) || isEqual(periodIds, noDatesSelectedValue)) {
        return this.tkey('tooltipMustHaveOnePeriod');
      }

      if (!this.selectedPeriodHasSingleDateRange) {
        return this.tkey('tooltipPeriodsMustHaveSameDates');
      }

      if (!selectedChain) {
        return this.tkey('tooltipChainMustBeSelected');
      }

      return null;
    },

    longDateFormat() {
      return this.getDateFormats.long;
    },

    selectedPeriodHasSingleDateRange() {
      return size(this.periodDateRange) === 2;
    },

    latestSelectableDate() {
      return this.toNearestSunday(this.latestSnapshotDate);
    },

    sizeSelectItems() {
      return Object.keys(SIZE_TYPES).map(key => ({
        value: SIZE_TYPES[key],
        title: this.$t(`sizeTypes.${key}`),
      }));
    },

    allowedSnapshotDates() {
      return this.snapshotDates;
    },

    allowedStartAndExcludedDates() {
      // must be Monday and <= latestSelectableDate
      return date => {
        const dateIsMonday = this.getIsoWeekday(date) === 1;
        const checkDateIsSameOrBefore = this.checkDateIsSameOrBefore(
          this.formatUtcDate(date),
          this.latestSelectableDate
        );

        return dateIsMonday && checkDateIsSameOrBefore;
      };
    },

    allowedEndDates() {
      // must be Sunday and <= latestSelectableDate
      return date => {
        const dateIsSunday = this.getIsoWeekday(date) === 7;
        const checkDateIsSameOrBefore = this.checkDateIsSameOrBefore(
          this.formatUtcDate(date),
          this.latestSelectableDate
        );

        return dateIsSunday && checkDateIsSameOrBefore;
      };
    },

    periodDateRange() {
      // We can explicitly choose to not use a period, or just have deselected periods.
      if (isEmpty(this.wpModelSelections.periodIds) || isEmpty(this.periodDates)) return [];

      const periodIds = this.wpModelSelections.periodIds;
      const noPeriodExplicitlySelected = periodIds.includes(null);
      const noPeriodsHaveBeenSelected = !size(periodIds);
      if (noPeriodExplicitlySelected || noPeriodsHaveBeenSelected) return new Set();

      const periods = periodIds
        .map(periodId => this.periodDates[periodId])
        .filter(periodObj => periodObj != null);

      const allDatesFound = periods.reduce((allDates, periodObj) => {
        allDates.add(periodObj.effectiveDatePlanned);
        allDates.add(periodObj.expiryDatePlanned);

        return allDates;
      }, new Set());

      return allDatesFound;
    },

    showScenarioSelection() {
      return !this.selectedWorkpackage.isTemplate;
    },

    previousWorkpackageScenarios() {
      return this.scenariosByWorkpackage[this.wpModelSelections.linkedPreviousWorkpackageId] || [];
    },

    isPreviousWorkpackageSearchDisabled() {
      return this.isWorkpackageSetupRunning || this.isRunningAutoPopulate;
    },

    isPreviousScenarioSearchDisabled() {
      return (
        !this.wpModelSelections.linkedPreviousWorkpackageId ||
        this.isWorkpackageSetupRunning ||
        this.isRunningAutoPopulate
      );
    },

    hasSelectedAssortmentGroupsAndRunWpSetup() {
      return (
        this.selectedWorkpackage &&
        this.selectedWorkpackage.selectedAssortmentGroupSettings &&
        this.selectedWorkpackage.selectedAssortmentGroupSettings.length &&
        // Check wp setup job has run
        this.selectedWorkpackage.jobs &&
        this.selectedWorkpackage.jobs.workpackageSetup &&
        this.selectedWorkpackage.jobs.workpackageSetup.status === 'finished'
      );
    },
  },

  watch: {
    longDateFormat() {
      // force rtls-calendar-picker to re-render with new date format
      this.nameKey = !this.nameKey;
    },
  },

  async created() {
    this.isLoading = true;
    try {
      await this.fetchSnapshotDates();
      this.initWpSetupData();

      if (this.hasPeriodIdsEnabled) {
        this.getPeriodIds();
      }

      // if there are linked previous workpackage / scenario, fetch them for the first time
      if (this.previousWorkpackageLinkingEnabled) {
        await this.setLinkedPreviousWorkpackageAndScenario();
      }
    } catch (e) {
      console.error(`Failed to initialise workpackage dates settings: ${e}`);
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    ...mapActions(useToolDataStore, ['getPeriodIds']),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useWorkpackagesStore, [
      'handleAutoAssortmentGroups',
      'updateWpDateSelections',
      'calculateDatesFromPeriodId',
      'updateWpModelSelections',
      'fetchSnapshotDates',
      'initWpSetupData',
      'fetchWorkpackages',
      'fetchSingleWorkpackage',
      'updateWpPreSetSelections',
      'updateSelectedAssortmentGroups',
    ]),
    ...mapActions(useWorkpackageProductsStore, ['setWorkpackageProducts']),
    ...mapActions(useWorkpackageScenarioSelectStore, ['fetchWorkpackageScenarios']),

    handlePerformancePreSetsUpdate(val) {
      // From performancePreSetOptions get the selected option given val
      const selectedOption = this.performancePeriodPreSetSelectOptions.find(
        option => option.id === val
      );
      if (!selectedOption) {
        throw new Error(`No performance period pre-set found for value: ${val}`);
      }
      this.updateWpPreSetSelections(selectedOption);
    },

    handleDateUpdate(value, field) {
      if (field === 'snapshotDate') {
        // Re-mount the picker to revert its display to the original date,
        // avoiding the timezone-shifted dateFormatted from the native Date object.
        this.nameKey = !this.nameKey;
        if (value === this.wpDateSelections.snapshotDate) return;

        this.pendingSnapshotDateValue = value;

        this.handleSnapshotDateChange(false);
        return;
      }

      this.updateWpDateSelections({ field, value });
    },

    async handleSnapshotDateChange(commit = false) {
      this.isSnapshotDateLoading = true;
      const results = await this.triggerDependencyTree({
        params: {
          change: 'workpackageSetupModified',
          updates: { global: { snapshotDate: this.pendingSnapshotDateValue } },
          commit,
          workpackageId: this.selectedWorkpackage._id,
        },
      });
      this.isSnapshotDateLoading = false;

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }

      this.applySnapshotDateChange();
    },

    applySnapshotDateChange() {
      this.selectedWorkpackage.stores = [];
      this.selectedWorkpackage.storeCount = null;
      this.selectedWorkpackage.productCount = null;
      this.selectedWorkpackage.selectedAssortmentGroupSettings = [];
      this.setWorkpackageProducts([]);
      this.updateSelectedAssortmentGroups([]);
      this.updateWpDateSelections({ field: 'snapshotDate', value: this.pendingSnapshotDateValue });
      this.pendingSnapshotDateValue = null;
      this.fetchSingleWorkpackage({
        workpackageId: this.selectedWorkpackage._id,
      });
      // Re-mount the picker to revert its display to the original date,
      // avoiding the timezone-shifted dateFormatted from the native Date object.
      this.nameKey = !this.nameKey;
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    updateModel(val, field) {
      this.updateWpModelSelections({ [field]: val });
    },

    async runAutoPopulate() {
      this.isRunningAutoPopulate = true;
      // only callable if there's a single set of dates
      const [start, end] = this.periodDateRange;
      const promises = [
        this.calculateDatesFromPeriodId(start, end),
        this.handleAutoAssortmentGroups(),
      ];

      try {
        if (this.previousWorkpackageLinkingEnabled && this.wpModelSelections.selectedChain) {
          const previousChainedWorkpackage = await this.getPreviousChainedWorkpackage({
            workpackageId: this.wpModelSelections._id,
            chainId: this.wpModelSelections.selectedChain,
          });
          await this.$refs.assortmentSearch.setSelectedItem(previousChainedWorkpackage);
        }
        await Promise.all(promises);
      } finally {
        this.isRunningAutoPopulate = false;
      }
    },

    // A handler to fetch matching possible previous workpackages by name
    onPreviousWorkpackageSearch(query) {
      return this.fetchWorkpackages({
        regexSearch: query,
        shouldGetTemplates: false,
        ignoreArchivedFlag: true,
        shouldUpdateState: false,
        shouldShowSpinner: false,
      });
    },

    isFinalScenario(scenarioId) {
      return this.finalScenarioIds.includes(scenarioId);
    },

    /* Update the model with the selected previous workpackage.
    If previousScenarioId is passed, set it as previous scenario if it's still valid.
    Otherwise, set the first available scenario for selected workpackage.
    */
    async handlePreviousWorkpackageChange(newVal, previousScenarioId) {
      if (!newVal) {
        // if no workpackage is selected, reset the workpackage and scenario selection
        this.updateModel(null, 'linkedPreviousWorkpackageId');
        this.updateModel(null, 'linkedPreviousScenarioId');
        return;
      }

      // update the state
      this.updateModel(newVal._id, 'linkedPreviousWorkpackageId');

      // if selected workpackage is not in store, make sure it's added to the store.
      // this could be moved to a store action in the future.
      const downloadedWorkpackages = [
        ...this.workpackages,
        ...this.archivedWorkpackages,
        ...this.additionalDownloadedWorkpackages,
      ];
      // check if the new workpackage is already in the store
      const isWorkpackageAlreadyDownloaded = downloadedWorkpackages.some(
        wp => wp._id === newVal._id
      );
      if (!isWorkpackageAlreadyDownloaded) {
        const missingWp = await this.fetchSingleWorkpackage({
          workpackageId: newVal._id,
          isStatelessFetch: true,
        });
        this.additionalDownloadedWorkpackages.push(missingWp);
      }

      // Now fetch scenarios for the previous workpackage and set the correct one as previous scenario.
      const workpackageScenarios = await this.fetchWorkpackageScenarios({ wpId: newVal._id });

      // if we passed a linked previous scenario and it's in the list, set it
      if (workpackageScenarios.length > 0) {
        const firstScenario = workpackageScenarios[0];
        const linkedScenario = workpackageScenarios.find(
          // find the scenario by ID, works with null as well as nothing will be found
          scenario => scenario._id === previousScenarioId
        );

        if (linkedScenario) {
          this.updateModel(previousScenarioId, 'linkedPreviousScenarioId');
        } else {
          const finalScenarioId = this.finalScenarioIds.find(scenarioId =>
            workpackageScenarios.some(s => s._id === scenarioId)
          );

          // default to the final scenario if present, otherwise just use the first scenario
          this.updateModel(finalScenarioId || firstScenario._id, 'linkedPreviousScenarioId');
        }
      } else {
        this.updateModel(null, 'linkedPreviousScenarioId');
      }
    },

    async setLinkedPreviousWorkpackageAndScenario() {
      // feetch and set the linked previous workpackage and scenario
      if (this.wpModelSelections.linkedPreviousWorkpackageId) {
        this.initialPreviousWorkpackage = await this.fetchSingleWorkpackage({
          workpackageId: this.wpModelSelections.linkedPreviousWorkpackageId,
          isStatelessFetch: true,
        });
        if (this.initialPreviousWorkpackage) {
          await this.handlePreviousWorkpackageChange(
            this.initialPreviousWorkpackage,
            this.wpModelSelections.linkedPreviousScenarioId
          );
        }
      }

      if (this.wpModelSelections.linkedPreviousScenarioId) {
        this.isLoadingScenarios = true;
        try {
          // fetch scenarios for the selected workpackage
          await this.fetchWorkpackageScenarios({
            wpId: this.wpModelSelections.linkedPreviousWorkpackageId,
          });
          // set the first scenario as selected
          const scenarios =
            this.scenariosByWorkpackage[this.wpModelSelections.linkedPreviousWorkpackageId] || [];
          const linkedScenario = scenarios.find(
            scenario => scenario._id === this.wpModelSelections.linkedPreviousScenarioId
          );
          if (linkedScenario) {
            this.updateModel(linkedScenario._id, 'linkedPreviousScenarioId');
          } else {
            // if the linked scenario is not found, select the first one
            this.updateModel(null, 'linkedPreviousScenarioId');
          }
        } catch (e) {
          console.error(`Failed to load workpackage scenarios: ${e}`);
        } finally {
          this.isLoadingScenarios = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.autodate-btn {
  height: 3rem !important;
}

.snapshot-date-warning-icon {
  font-size: 2.4rem !important;
}

.dates-selection {
  background-color: $assortment-workpackage-frame-bg-blue;

  &__input--custom {
    max-width: calc(100% - 16px);
    width: 80px;

    :deep() {
      .v-field {
        height: 32px !important;
      }
    }
  }

  &__input-heading {
    margin-bottom: 8px;
    font-size: 1.2rem;

    span {
      font-weight: normal;
    }
  }

  &__input {
    :deep(.v-input__slot) {
      background: #fff;
      border-bottom: 1px solid $assortment-primary-colour !important;
      width: 100px;
    }

    .v-input,
    .v-select {
      margin: 0;
      padding: 0;
      width: 60%;
    }

    &--longer {
      align-items: center;
      display: flex;
      justify-content: center;
      margin-right: 14px;

      .v-input {
        width: 90% !important;
      }
    }

    &--custom {
      height: 20px;
    }
  }

  &__input-container {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 14px;

    span {
      font-size: 1.2rem;
      margin-right: 23px;
    }
  }

  &__group-button {
    border-color: $assortment-primary-colour !important;
    height: 32px !important;
    flex: 1;

    :deep(:not(.v-btn--active)) {
      background-color: transparent !important;
    }
  }

  &__exclude-container {
    align-items: flex-start;
    display: flex;
    font-weight: normal;
    justify-content: flex-start;

    h3 {
      margin-top: 5px;
      margin-right: 15px;
      font-weight: normal;
    }
  }

  &__maximum-trend-container {
    .rtls-text-field-container {
      width: 100px;
    }
  }

  &__input_startdate {
    font-weight: bold;
  }
}

.border-card {
  border-top: 1px solid $assortment-divider-colour-darker;
}

.v-card {
  padding-bottom: 0 !important;
}

:deep(.v-text-field__slot input) {
  padding-left: 5px;
}

:deep(.v-item-group) {
  width: 100%;
}

// Remove the empty space which appears when at least one list item has an icon prepended.
// Too much leading space appears in the linked scenario dropdown, so we control it ourselves via margins instead.
:deep(.v-list-item__prepend > .v-list-item__spacer) {
  width: 0;
}

// Remove the default padding because this applies even when the list item selected has no prepended icon.
// Padding is added directly to the icon instead.
:deep(.v-field--prepended) {
  padding: 0;
}

:deep(.v-chip) {
  background: $assortment-workpackage-chip-colour !important;
}
</style>
